import { API_URL } from "@/config/constants";
import {
    QueryClient,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";
import { invalidateOrganizationGroupQueries } from "../organization-groups/organization-groups";
import {
    DraftUpdatePayload,
    IDraftDetail,
    IDraftListItem,
    IRefinementDiff,
} from "./checklist-generation.types";
import {
    IClauseChecklistTopic,
    IClauseChecklistTopicCreate,
    IClauseChecklistTopicClausesUpdate,
} from "./clause-checklists.types";

const API = `${API_URL}/clause-checklists/drafts`;

export type {
    DraftTopicUpdate,
    DraftUpdatePayload,
} from "./checklist-generation.types";

export const adminDraftQueryKeys = {
    lists: ["admin-drafts"] as const,
    detail: (id: number) => ["draft-detail", id] as const,
};

export class DraftUpdateRefreshUnconfirmedError extends Error {
    constructor(
        readonly confirmedDraft: IDraftDetail,
        readonly cause: unknown
    ) {
        super("The draft was saved, but the view could not be refreshed.");
        this.name = "DraftUpdateRefreshUnconfirmedError";
    }
}

export class DraftPublishRefreshUnconfirmedError extends Error {
    constructor(
        readonly confirmedPublication: { id: number },
        readonly cause: unknown
    ) {
        super(
            "The checklist was published, but the view could not be refreshed."
        );
        this.name = "DraftPublishRefreshUnconfirmedError";
    }
}

export const listDrafts = async (): Promise<IDraftListItem[]> => {
    const response = await axios.get<IDraftListItem[]>(API);
    return response.data;
};

export const getDraft = async (id: number): Promise<IDraftDetail> => {
    const response = await axios.get<IDraftDetail>(`${API}/${id}`);
    return response.data;
};

export const updateDraft = async (
    id: number,
    payload: DraftUpdatePayload
): Promise<IDraftDetail> => {
    const response = await axios.put<IDraftDetail>(`${API}/${id}`, payload);
    return response.data;
};

export const deleteDraft = async (id: number): Promise<void> => {
    await axios.delete(`${API}/${id}`);
};

export const publishDraft = async (id: number): Promise<{ id: number }> => {
    const response = await axios.post<{ id: number }>(`${API}/${id}/publish`);
    return response.data;
};

const invalidateAdminDraftReads = (
    queryClient: QueryClient,
    id: number,
    detailRefetch: boolean = true
) => [
    queryClient.invalidateQueries(
        { queryKey: adminDraftQueryKeys.lists },
        { throwOnError: true }
    ),
    queryClient.invalidateQueries(
        {
            queryKey: adminDraftQueryKeys.detail(id),
            ...(detailRefetch ? {} : { refetchType: "none" as const }),
        },
        { throwOnError: true }
    ),
];

export const useDrafts = () =>
    useQuery({
        queryKey: adminDraftQueryKeys.lists,
        queryFn: listDrafts,
    });

export const useDraft = (id: number) =>
    useQuery({
        queryKey: adminDraftQueryKeys.detail(id),
        queryFn: () => getDraft(id),
    });

export const useUpdateDraft = (id: number) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload: DraftUpdatePayload) => updateDraft(id, payload),
        onSuccess: async (draft) => {
            const [, detail] = await Promise.allSettled(
                invalidateAdminDraftReads(queryClient, id)
            );
            if (detail.status === "rejected") {
                throw new DraftUpdateRefreshUnconfirmedError(
                    draft,
                    detail.reason
                );
            }
        },
    });
};

export const usePublishDraft = (id: number) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => publishDraft(id),
        onSuccess: async (publication) => {
            try {
                await Promise.all([
                    ...invalidateAdminDraftReads(queryClient, id, false),
                    invalidateOrganizationGroupQueries(queryClient, {
                        throwOnError: true,
                    }),
                ]);
            } catch (cause) {
                throw new DraftPublishRefreshUnconfirmedError(
                    publication,
                    cause
                );
            }
        },
    });
};

// --- Draft Topic/Clause Management ---

export const addTopicToDraft = async (
    draftId: number,
    body: IClauseChecklistTopicCreate
): Promise<IClauseChecklistTopic> => {
    const response = await axios.post<IClauseChecklistTopic>(
        `${API}/${draftId}/topics`,
        body
    );
    return response.data;
};

export const removeTopicFromDraft = async (
    draftId: number,
    topicId: number
): Promise<void> => {
    await axios.delete(`${API}/${draftId}/topics/${topicId}`);
};

export const updateDraftTopicClauses = async (
    draftId: number,
    body: IClauseChecklistTopicClausesUpdate
): Promise<void> => {
    await axios.put(`${API}/${draftId}/topics/clauses`, body);
};

export interface ApplyRefinementResponse {
    success: boolean;
    change_summary: string;
}

export const applyRefinement = async (
    draftId: number,
    diff: IRefinementDiff
): Promise<ApplyRefinementResponse> => {
    const response = await axios.post<ApplyRefinementResponse>(
        `${API}/${draftId}/apply-refinement`,
        diff
    );
    return response.data;
};

export const resolveGapSuggestion = async (
    draftId: number,
    gapTitle: string,
    clauseId: number
): Promise<void> => {
    await axios.patch(`${API}/${draftId}/gaps/resolve`, {
        gap_title: gapTitle,
        clause_id: clauseId,
    });
};
